import { RiAddLine, RiKey2Line } from '@remixicon/react';
import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { RouteElement } from '@shared/router';
import * as history from '@shared/router/location';

import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';
import { Symbol } from '@playbook/atoms/symbol';
import { Tab } from '@playbook/atoms/tab';
import { TabGroup } from '@playbook/compositions/tab-group';

import { useKeys } from '@portal/pkg/api/keys';
import { Action, type Key } from '@portal/pkg/api/types';
import * as routes from '@portal/pkg/routes';
import { useSession } from '@portal/pkg/session';
import { CopyableValue } from '@portal/views/_shared/code-block';
import {
	PageHeader,
	PageSection,
	PortalPage,
} from '@portal/views/_shared/page';
import { PageLoader } from '@portal/views/_shared/page-loader';
import { KeyRow } from '@portal/views/keys/KeyRow';
import { LoadFailed } from '@portal/views/keys/LoadFailed';
import { can, isActive } from '@portal/views/keys/_shared';

import css from './styles.module.css';

const Description =
	'A key lets your server call the 360Player public API. It carries the access you grant it and nothing more.';

export default function KeysPage(): RouteElement {
	const { organization } = useSession();
	const mayCreate = can(organization, Action.CreateKey);

	return (
		<PortalPage>
			<PageHeader
				title="API keys"
				description={Description}
				actions={
					mayCreate && (
						<Button
							variant="primary"
							label="New key"
							leadingIcon={<RiAddLine />}
							onClick={() => history.push(routes.KeyCreate())}
						/>
					)
				}
			/>
			{organization?.publicId && (
				<PageSection>
					<CopyableValue
						name="organizationId"
						value={organization.publicId}
						label="Organization ID"
						help="The group_id a token request sends to work in your organization."
					/>
				</PageSection>
			)}
			{/*
			 *	Its own boundary, so a refetch after a rotation swaps the list
			 *	for a loader rather than blanking the page — and so a failure
			 *	offers a retry instead of escaping to the router's boundary,
			 *	which only resets on navigation.
			 */}
			<ErrorBoundary FallbackComponent={LoadFailed}>
				<Suspense fallback={<PageLoader />}>
					<KeyList mayCreate={mayCreate} />
				</Suspense>
			</ErrorBoundary>
		</PortalPage>
	);
}

function KeyList({ mayCreate }: { mayCreate: boolean }): ReactNode {
	const keys = useKeys();

	if (keys.length === 0) {
		return <NoKeysYet mayCreate={mayCreate} />;
	}

	const active = keys.filter(isActive);
	const inactive = keys.filter((key) => !isActive(key));

	return (
		<TabGroup name="keyStatus" label="Which keys to show">
			<Tab
				value="active"
				defaultChecked
				label={`Active (${active.length})`}
				panel={<KeyCards keys={active} empty="No active keys." />}
			/>
			<Tab
				value="inactive"
				label={`Inactive (${inactive.length})`}
				panel={
					<KeyCards
						keys={inactive}
						empty="Nothing has expired or been revoked."
					/>
				}
			/>
		</TabGroup>
	);
}

function KeyCards({ keys, empty }: { keys: Key[]; empty: string }): ReactNode {
	if (keys.length === 0) {
		return <p className={css.empty}>{empty}</p>;
	}

	return (
		<ul className={css.cards}>
			{keys.map((key) => (
				<li key={key.id}>
					<KeyRow forKey={key} />
				</li>
			))}
		</ul>
	);
}

function NoKeysYet({ mayCreate }: { mayCreate: boolean }): ReactNode {
	return (
		<Card>
			<div className={css.blank}>
				<Symbol size="xl" rounded icon={<RiKey2Line />} />
				<h2>No API keys yet</h2>
				<p>
					{mayCreate
						? 'The key pair is made in your browser. You keep the private half; we only ever store the public one.'
						: 'Nobody has made one. Creating a key needs write access to the organization’s API keys.'}
				</p>
				{mayCreate && (
					<Button
						variant="primary"
						label="Create your first key"
						leadingIcon={<RiAddLine />}
						onClick={() => history.push(routes.KeyCreate())}
					/>
				)}
			</div>
		</Card>
	);
}
